import React, { useEffect, useState } from 'react';
import {
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import { useAdminMarkingCodes } from './hooks/useAdminMarkingCodes';
import { MarkingCodesToolbar } from './components/MarkingCodesToolbar';
import { MarkingCodesTable } from './components/MarkingCodesTable';
import { MarkingCodesExportDialog } from './components/MarkingCodesExportDialog';
import AdminDocumentViewDialog from '../AdminDocumentViewDialog';
import { DocumentDetails } from '../AdminDocuments';
import { API_BASE } from './constants';

export const AdminMarkingCodes: React.FC = () => {
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [docDialogOpen, setDocDialogOpen] = useState<boolean>(false);

  const {
    items,
    totalCount,
    isLoading,
    filters,
    page,
    pageSize,
    pharmacies,
    storages,
    statuses,
    types,
    notification,
    setPage,
    setPageSize,
    handleRefresh,
    handleExport,
    handleFilterChange,
    handleSort,
    handleResetFilters,
    closeNotification
  } = useAdminMarkingCodes();

  useEffect(() => {
    document.title = 'Коды маркировки (КМ) | Zerde Admin';
  }, []);

  const handleOpenDocument = async (docId: number) => {
    if (!docId) return;
    setDetailsLoading(true);
    setDocDialogOpen(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/documents/${docId}`);
      if (response.data.success) {
        setSelectedDoc(response.data.document);
      }
    } catch (err: any) {
      console.error('Ошибка при загрузке документа:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDocDialog = () => {
    setDocDialogOpen(false);
    setSelectedDoc(null);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Тулбар с фильтрами по дате, складу, аптеке, типу, статусу и кнопками */}
      <MarkingCodesToolbar
        filters={filters}
        isLoading={isLoading}
        pharmacies={pharmacies}
        storages={storages}
        statuses={statuses}
        types={types}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        onExport={() => setExportDialogOpen(true)}
        onResetFilters={handleResetFilters}
      />

      {/* Таблица с сортировкой и кликабельными номерами накладных */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <MarkingCodesTable
          items={items}
          totalCount={totalCount}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          orderBy={filters.orderBy}
          order={filters.order}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          onDocumentClick={handleOpenDocument}
        />
      </Box>

      {/* Модальное окно детальной настройки экспорта */}
      <MarkingCodesExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        initialFilters={filters}
        pharmacies={pharmacies}
        storages={storages}
        statuses={statuses}
        types={types}
      />

      {/* Просмотр документа */}
      <AdminDocumentViewDialog
        open={docDialogOpen}
        document={selectedDoc}
        loading={detailsLoading}
        onClose={handleCloseDocDialog}
        onRefreshDocument={handleOpenDocument}
      />

      {/* Уведомление */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminMarkingCodes;
